/**
 * V-HEALTHY LANGUAGE CONTEXT PROVIDER
 *
 * Handles language switching and URL synchronization
 * This component should be used in the main layout components
 */

import React, { createContext, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from '../redux/store';
import { updateLangData } from '../redux/langugage/actions';

interface LanguageContextType {
  currentLanguage: 'en' | 'fr';
  switchLanguage: (language: 'en' | 'fr') => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const { type: currentLanguage } = useSelector((state: AppState) => state.language);

  const switchLanguage = (language: 'en' | 'fr') => {
    // Update Redux state - URL will be handled by the routing system
    dispatch(updateLangData({ type: language }));
  };

  const value: LanguageContextType = {
    currentLanguage: (currentLanguage || 'en') as 'en' | 'fr',
    switchLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
